import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Spinner from "sushiweb/Spinner/Spinner";
import { ChevronRight, CircleHelp, ImageOff } from "lucide-react";
import { handleActionType, setupJSBridgeActionHandler } from "helpers/app_router";
import { ACTION_TYPES } from "utils/constants/app_router";
import {
  GRN_STATUS_COMPLETED, ORDER_STATUS_DISPATCHED,
  PAYMENT_STATUS_PENDING, PO_ORDER_TAG,
  PO_STATUS_APPROVED, SELLER_ACTION_STATUS_COMPLETED, SELLER_ACTION_STATUS_PENDING,
  SELLER_STATUS_NON_APPROVED,
  SERVICE_PO_SOURCE_PENDING_INVOICES,
  SERVICE_PO_SOURCE_REJECTED_INVOICES,
  STATUS_BEGIN,
  STATUS_DONE,
  STATUS_FAIL,
  PO_ORDER_TAG_MANDI_DD,
  PO_ORDER_TAG_DIRECT_DELIVERY,
} from "utils/constants/others";
import { strings } from "utils/constants/strings";
import Header from "../Header/Header";
import { Loader } from "../Common/Loader";
import {
  Badge,
  HeroCarousel,
  HeroCard,
  HeroCopy,
  HeroCta,
  HeroDots,
  HeroEyebrow,
  HeroSlide,
  HeroTrack,
  HeroTitle,
  HeroViewport,
  HomeContent,
  HomeSkeleton,
  HomeSurface,
  ManageOrdersCard,
  NotificationBadge,
  OrderTile,
  OrdersGrid,
  SectionTitle,
  SkeletonBlock,
  TileIcon,
  TileIconFallback,
  QuickActionRow,
  QuickActionsCard,
  QuickActionsTitle,
} from "./styles.homepage";
import useVendorStore from "./useVendorStore";
import { SushiModal, FlexColumn, Button, WhiteWrapper, SeparatorLine } from "../Common/styles.common";
import moment from "moment/moment";
import ShowToast from "../Common/Toast";
import Footer from "../Footer/Footer";
import { trackClick } from "utils/tracking";
import { TRACKING_COMPONENTS, TRACKING_PAGES } from "utils/constants/trackingConstants";
import useAuthStore from "../Auth/useAuthStore";
import useBiddingStore from "../Bids/useBiddingStore";

const BANNER_AUTOPLAY_DELAY = 4000;
const HOME_SECTION_TYPES = {
  bannerCarousel: "BANNER_CAROUSEL",
  iconGrid: "ICON_GRID",
};
const HOME_ITEM_IDS = {
  purchaseOrders: "purchase_orders",
  directDelivery: "direct_delivery",
  grnPayments: "grn_payments",
};

const HomePage = () => {
  const outletId = window.localStorage.getItem("outlet_id");
  const sellerOutletId = Number(outletId);
  const hasValidSellerOutletId = Number.isInteger(sellerOutletId) && sellerOutletId > 0;
  const getPurchaseOrders = useVendorStore((state) => state.getPurchaseOrders);
  const getPurchaseOrderStatus = useVendorStore(
    (state) => state.getPurchaseOrderStatus
  );
  const getPoGrnMappings = useVendorStore((state) => state.getPoGrnMappings);
  const getPoGrnMappingStatus = useVendorStore(
    (state) => state.getPoGrnMappingStatus
  );
  const getHomePagePopUp = useVendorStore((state) => state.getHomePagePopUp);
  const poCount = useVendorStore((state) => state.poCount);
  const grnCount = useVendorStore((state) => state.grnCount);
  const searchServicePurchaseOrders = useVendorStore((state) => state.searchServicePurchaseOrders);
  const searchServicePurchaseOrdersStatus = useVendorStore((state) => state.searchServicePurchaseOrdersStatus);
  const getHomePageSections = useVendorStore((state) => state.getHomePageSections);
  const homePageSections = useVendorStore((state) => state.homePageSections);
  const getHomePageSectionsStatus = useVendorStore((state) => state.getHomePageSectionsStatus);
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = useState(false);
  const [po, setPo] = useState([]);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  const [pendingDeliveryCount, setPendingDeliveryCount] = useState(0);
  const sellerDetails = useVendorStore((state) => state.sellerDetails);
  const homePagePopUp = useVendorStore((state) => state.homePagePopUp);
  const updateHomePagePopUp = useVendorStore((state) => state.updateHomePagePopUp);
  const getSellerDetails = useVendorStore((state) => state.getSellerDetails);
  const [showSubHeader, setShowSubHeader] = useState(false);
  const [currentSellerDetail, setCurrentSellerDetail] = useState({});
  const [allSellersDetails, setAllSellersDetails] = useState([]);
  const [showButtonLoader, setShowButtonLoader] = useState(false);
  const [showTncModal, setShowTncModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const updateHomePagePopUpStatus = useVendorStore((state) => state.updateHomePagePopUpStatus);
  const fetchAppConfigs = useVendorStore((state) => state.fetchAppConfigs);
  const [pendingInvoicesCount, setPendingInvoicesCount] = useState(0);
  const [rejectedInvoicesCount, setRejectedInvoicesCount] = useState(0);
  const [isNonTradeVendor, setIsNonTradeVendor] = useState(false);
  const [selectedBannerIndex, setSelectedBannerIndex] = useState(0);
  const [bannerProgress, setBannerProgress] = useState(0);
  const [failedIconIds, setFailedIconIds] = useState({});
  const bannerAnimationFrameRef = useRef();
  const [heroEmblaRef, heroEmblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
  });
  const { biddingHomeData, getBiddingHomeData } = useBiddingStore();
  const bidsCount = biddingHomeData.counts?.live || 0;
  const bannerBgUrl = biddingHomeData.images?.SELLER_BIDDING_BANNER || null;
  const bidsLogoUrl = biddingHomeData.images?.SELLER_BIDDING_LOGO || null;

  useEffect(() => {
    const params = {
      phone_number: window.localStorage.getItem("phone_number"),
      check_for_dn_tncs: true,
    };

    getSellerDetails(params);
    getBiddingHomeData();
  }, []);

  // Setup JSBridge for native app communication
  useEffect(() => {
    setupJSBridgeActionHandler(navigate, {
      showToast: (msg) => {
        setToastMsg(msg);
        setShowToast(true);
      },
    });

    // Check if there's a pending action from before the app was ready
    if (window.__pendingJSBridgeAction) {
      const pendingAction = window.__pendingJSBridgeAction;
      window.__pendingJSBridgeAction = null;
      if (typeof window.handleActionTypeFromNative === 'function') {
        window.handleActionTypeFromNative(pendingAction);
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (sellerDetails?.seller_outlet_details?.length > 0) {
      const currentOutlet = sellerDetails.seller_outlet_details.find(item => item.outlet_id == outletId);
      const isNonTradeVendorOutlet = currentOutlet?.outlet_type == strings.nonTradeVendor;

      if (isNonTradeVendorOutlet) {
        setIsNonTradeVendor(true);
        searchServicePurchaseOrders({
          vendor_id: parseInt(outletId),
          is_doc_uploaded: false,
          page_number: 1,
          page_size: 20
        }, storeServicePurchaseOrderData, SERVICE_PO_SOURCE_PENDING_INVOICES);

        searchServicePurchaseOrders({
          vendor_id: parseInt(outletId),
          is_doc_uploaded: true,
          page_number: 1,
          page_size: 20
        }, storeServicePurchaseOrderData, SERVICE_PO_SOURCE_REJECTED_INVOICES);
      } else {
        setIsNonTradeVendor(false);
        // For other outlet types, use the original API calls
        getPurchaseOrders({
          vendor_id: outletId,
          po_status: PO_STATUS_APPROVED,
          seller_status: SELLER_STATUS_NON_APPROVED,
          fetch_po_with_no_grns: true,
        });
        getPoGrnMappings({
          grn_status: GRN_STATUS_COMPLETED,
          seller_status: SELLER_STATUS_NON_APPROVED,
          outlet_id: outletId,
          payment_status: PAYMENT_STATUS_PENDING,
          exclude_purchase_order_tags: `${PO_ORDER_TAG_MANDI_DD},${PO_ORDER_TAG_DIRECT_DELIVERY}`
        }, true);
        getPurchaseOrders({
          vendor_id: outletId,
          is_item_required: true,
          order_tag: PO_ORDER_TAG,
          fetch_invoice: true,
          fetch_po_with_no_seller_invoice: true
        }, true, storePurchaseOrderData, "INVOICE");
        getPurchaseOrders({
          vendor_id: outletId,
          is_item_required: true,
          order_tag: PO_ORDER_TAG,
          fetch_invoice: true,
          fetch_po_with_no_seller_invoice: false,
          order_status: `${ORDER_STATUS_DISPATCHED},${SELLER_ACTION_STATUS_COMPLETED}`,
          seller_action_status: SELLER_ACTION_STATUS_PENDING,
          is_delivery_details_required: true
        }, true, storePurchaseOrderData, "PENDING");
      }
    }
  }, [sellerDetails]);

  useEffect(() => {
    if (homePagePopUp?.heading?.length > 0) {
      setShowTncModal(true)
    }
  }, [homePagePopUp])

  useEffect(() => {
    if (updateHomePagePopUpStatus === STATUS_DONE) {
      setShowButtonLoader(false)
      setShowToast(true)
    }
    if (updateHomePagePopUpStatus === STATUS_FAIL) {
      setShowButtonLoader(false)
      setToastMsg("Error in updating the status. Please try again later.")
      setShowToast(true)
    }
  }, [updateHomePagePopUpStatus])

  useEffect(() => {
    if (!hasValidSellerOutletId) return;

    getHomePagePopUp({
      seller_outlet_id: sellerOutletId
    })
  }, [getHomePagePopUp, hasValidSellerOutletId, sellerOutletId])

  useEffect(() => {
    if (!hasValidSellerOutletId) return;

    getHomePageSections({
      seller_outlet_id: sellerOutletId
    });
  }, [getHomePageSections, hasValidSellerOutletId, sellerOutletId]);

  useEffect(() => {
    if (sellerDetails?.seller_outlet_details?.length > 0) {
      setShowSubHeader(true);
      const allSellerOutletDetails = [
        ...(sellerDetails?.seller_outlet_details || []),
      ];
      const index = allSellerOutletDetails.findIndex(item => item.outlet_id == outletId); // Find the index of current outlet

      setCurrentSellerDetail(sellerDetails?.seller_outlet_details[index])
      let currentOutlet = sellerDetails?.seller_outlet_details[index]
      if (index !== -1 && index !== 0) {
        const itemToMove = allSellerOutletDetails.splice(index, 1); // Remove the item from its current position
        const updatedSellerList = [...itemToMove, ...allSellerOutletDetails]; // Concatenate the item to the beginning of the array
        setAllSellersDetails(updatedSellerList)
      } else {
        setAllSellersDetails(allSellerOutletDetails)
      }
      if (!isNotFarmerOutlet(currentOutlet)) {
        window.localStorage.setItem("seller-type", strings.farmerOutlet)
      } else {
        window.localStorage.removeItem("seller-type")
      }

      if (!window.localStorage.getItem('referrer_platform')?.length) {
        fetchAppConfigs({
          app_mode: strings.appModeSeller,
        })
      }
    }
  }, [sellerDetails])

  function storePurchaseOrderData(data, source) {
    if (source === "INVOICE") {
      setPendingInvoiceCount(data.order_count);
    } else if (source === "PENDING") {
      setPendingDeliveryCount(data.order_count);
      setPo(data.purchase_orders?.slice(0, Math.min(data.order_count, 3)) || []);
    }
  }

  function storeServicePurchaseOrderData(data, source) {
    if (source === SERVICE_PO_SOURCE_PENDING_INVOICES) {
      const count = data.purchase_orders?.length || 0;
      setPendingInvoicesCount(count);
    } else if (source === SERVICE_PO_SOURCE_REJECTED_INVOICES) {
      const count = data.po_entities?.length || 0;
      setRejectedInvoicesCount(count);
    }
  }

  useEffect(() => {
    if (getPurchaseOrderStatus === STATUS_BEGIN || getPoGrnMappingStatus === STATUS_BEGIN || searchServicePurchaseOrdersStatus === STATUS_BEGIN) {
      setShowLoader(true);
    }
    if (getPurchaseOrderStatus === STATUS_DONE && getPoGrnMappingStatus === STATUS_DONE) {
      setShowLoader(false);
    }
    if (searchServicePurchaseOrdersStatus === STATUS_DONE) {
      setShowLoader(false);
    }
  }, [getPurchaseOrderStatus, getPoGrnMappingStatus, searchServicePurchaseOrdersStatus]);

  const checkPendingGRNs = () => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.PENDING_GRNS);
    navigate("grn/pending");
  };

  const checkPendingPOs = () => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.PENDING_POS);
    navigate("po/pending");
  };

  const checkAllDDPOs = () => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.ALL_DD_POS);
    navigate("ddPo/all");
  };

  const checkBids = () => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.BIDS);
    navigate("bids/all");
  };

  const checkPendingInvoices = () => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.PENDING_INVOICES);
    navigate(strings.SERVICE_POS_ROUTE);
  };

  const checkRejectedInvoices = () => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.REJECTED_INVOICES);
    navigate(strings.SERVICE_POS_ROUTE, { state: { defaultTab: 1 } });
  };

  const checkBiddings = () => {
    trackClick(TRACKING_PAGES.HOME, "BIDDINGS");
    navigate("bids/all");
  };

  const checkPlanShipment = () => {
    trackClick(TRACKING_PAGES.HOME, "PLAN_SHIPMENT");
    navigate("plan-shipment");
  };

  const naviagetToDelieryPage = (poId, source) => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.DD_PO_DETAILS_POD);
    navigate(`/ddPo/details/${source}/${poId}`);
  };

  const handleProfileNavigate = () => {
    navigate("/profile");
  };

  const handleManageUsersNavigate = () => {
    navigate("/manage-users");
  };

  const handleDownloadLabelsNavigate = () => {
    navigate("/download-labels");
  };

  const handleGetHelp = () => {
    window.location.href = "mailto:help@hyperpure.com";
  };

  const handleLogout = () => {
    trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.LOGOUT_CONFIRM);
    useAuthStore.getState().signOut();
  };

  const isNotFarmerOutlet = (currentSellerDetail) => {
    if (currentSellerDetail?.outlet_type == strings.farmerOutlet) {
      return false;
    }
    return true;
  };

  const getDateMonthFromString = (timestamp, value) => {
    const monthInt = moment(new Date(timestamp)).format("MM");
    const day = moment(new Date(timestamp)).format("DD").toString();
    const monthStr = moment.months(parseInt(monthInt, 10) - 1);

    if (value === "day") {
      return day;
    }

    if (value === "month") {
      return monthStr.slice(0, 3);
    }

    return "";
  };

  const calculateTonnage = (itemsList = []) => {
    let total = 0;
    for (const item of itemsList) {
      total += item.quantity_ordered * item.weight_per_packet;
    }

    return total > 999 ? (total / 1000).toFixed(2) : total.toFixed(2);
  };

  const getWeightUnit = (itemsList = []) => {
    let total = 0;
    for (const item of itemsList) {
      total += item.quantity_ordered * item.weight_per_packet;
    }

    return total > 999 ? "tonne" : "kg";
  };

  const handleConfirmGrnCTA = (grnId) => {
    {
      trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.CONFIRM_GRN_CTA);
      setShowButtonLoader(true)
      updateHomePagePopUp(homePagePopUp?.cta?.Action?.content?.Body
      )
    }
    setShowTncModal(false)
  }

  const hasHomePagePopup = homePagePopUp?.heading?.length > 0;

  const normalizeCount = (count) => {
    const parsed = Number(count);

    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatBadgeCount = (count) => {
    const normalizedCount = normalizeCount(count);

    return normalizedCount > 99 ? "99+" : normalizedCount;
  };

  const renderBadge = (count) => {
    if (normalizeCount(count) <= 0) return null;
    return <Badge>{formatBadgeCount(count)}</Badge>;
  };

  const renderNotificationBadge = () => (
    <NotificationBadge>
      <img src="/logos/bang.svg" alt="" />
    </NotificationBadge>
  );

  const isGrnPaymentsItem = (item) => (
    item?.id === HOME_ITEM_IDS.grnPayments || item?.action_type === ACTION_TYPES.SELLER_GRN_PENDING
  );

  const directDeliveryCount = pendingInvoiceCount + pendingDeliveryCount;
  const servicePoCount = pendingInvoicesCount + rejectedInvoicesCount;
  const poTileCount = isNonTradeVendor ? servicePoCount : poCount;

  const handleGrnAndPaymentsClick = () => {
    if (hasHomePagePopup) {
      trackClick(TRACKING_PAGES.HOME, TRACKING_COMPONENTS.HOME_PAGE.PENDING_GRNS);
      setShowTncModal(true);
      return;
    }

    if (!grnCount) return;

    checkPendingGRNs();
  };
  const isGrnPaymentsDisabled = !hasHomePagePopup && !grnCount;
  const isGrnPendingAction = (actionType) => actionType === ACTION_TYPES.SELLER_GRN_PENDING;

  const sortedHomePageSections = [...(homePageSections || [])].sort(
    (a, b) => (a?.display_order || 0) - (b?.display_order || 0)
  );
  const bannerSection = sortedHomePageSections.find((section) => section?.type === HOME_SECTION_TYPES.bannerCarousel);
  const iconGridSection = sortedHomePageSections.find((section) => section?.type === HOME_SECTION_TYPES.iconGrid);
  const bannerSectionItems = [...(bannerSection?.items || [])].sort(
    (a, b) => (a?.display_order || 0) - (b?.display_order || 0)
  );
  const iconGridItems = [...(iconGridSection?.items || [])].sort(
    (a, b) => (a?.display_order || 0) - (b?.display_order || 0)
  );

  const bannerSlides = bannerSectionItems.filter(
    (banner) => banner?.image_url || banner?.title || banner?.subtitle || banner?.cta_text
  );
  const isHomeSectionsLoading = getHomePageSectionsStatus === STATUS_BEGIN;
  const shouldShowHomeSkeleton = isHomeSectionsLoading && sortedHomePageSections.length === 0;
  const shouldShowHeroCarousel = bannerSlides.length > 0;

  const handleBannerSelect = useCallback(() => {
    if (!heroEmblaApi) return;

    setSelectedBannerIndex(heroEmblaApi.selectedScrollSnap());
    setBannerProgress(0);
  }, [heroEmblaApi]);

  useEffect(() => {
    if (!heroEmblaApi) return undefined;

    heroEmblaApi.on("select", handleBannerSelect);
    heroEmblaApi.on("reInit", handleBannerSelect);
    handleBannerSelect();

    return () => {
      heroEmblaApi.off("select", handleBannerSelect);
      heroEmblaApi.off("reInit", handleBannerSelect);
    };
  }, [heroEmblaApi, handleBannerSelect]);

  useEffect(() => {
    if (!heroEmblaApi) return;

    setSelectedBannerIndex(0);
    setBannerProgress(0);
    heroEmblaApi.reInit();
    heroEmblaApi.scrollTo(0, true);
  }, [bannerSlides.length, heroEmblaApi]);

  useEffect(() => {
    if (!heroEmblaApi || bannerSlides.length <= 1) {
      setBannerProgress(0);
      return undefined;
    }

    let startTime;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / BANNER_AUTOPLAY_DELAY, 1);
      setBannerProgress(progress);

      if (progress >= 1) {
        setBannerProgress(0);
        heroEmblaApi.scrollNext();
        return;
      }

      bannerAnimationFrameRef.current = window.requestAnimationFrame(tick);
    };

    setBannerProgress(0);
    bannerAnimationFrameRef.current = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(bannerAnimationFrameRef.current);
    };
  }, [heroEmblaApi, selectedBannerIndex, bannerSlides.length]);

  const handleBannerDotClick = (index) => {
    setBannerProgress(0);
    heroEmblaApi?.scrollTo(index);
  };

  const handleBannerClick = (banner) => {
    if (!banner?.action_type) return;

    handleActionType(banner.action_type, banner.action_data, navigate, {
      actionHandlers: {
        [ACTION_TYPES.SELLER_GRN_PENDING]: handleGrnAndPaymentsClick,
      },
    });
  };

  const getTileCount = (itemOrId) => {
    const item = typeof itemOrId === "object" ? itemOrId : { id: itemOrId };
    const tileId = item?.id;

    switch (tileId) {
      case HOME_ITEM_IDS.purchaseOrders:
        return normalizeCount(poTileCount);
      case HOME_ITEM_IDS.directDelivery:
        return normalizeCount(directDeliveryCount);
      case HOME_ITEM_IDS.grnPayments:
        return normalizeCount(grnCount);
      case HOME_ITEM_IDS.biddings:
      case HOME_ITEM_IDS.biddingsBanner:
        return normalizeCount(bidsCount);
      default:
        break;
    }

    switch (item?.action_type) {
      case ACTION_TYPES.SELLER_PO_LIST:
        return normalizeCount(poTileCount);
      case ACTION_TYPES.SELLER_DD_PO_LIST:
        return normalizeCount(directDeliveryCount);
      case ACTION_TYPES.SELLER_GRN_PENDING:
        return normalizeCount(grnCount);
      case ACTION_TYPES.SELLER_BIDDINGS:
        return normalizeCount(bidsCount);
      default:
        return 0;
    }
  };

  const handleSectionTileClick = (item) => {
    if (!item?.action_type) return;

    handleActionType(item.action_type, item.action_data, navigate, {
      actionHandlers: {
        [ACTION_TYPES.SELLER_GRN_PENDING]: () => {
          handleGrnAndPaymentsClick();
        },
      },
    });
  };

  const orderTiles = iconGridItems.length > 0
    ? iconGridItems.map((item) => {
      if (!item?.title) return null;
      const isGrnTile = isGrnPendingAction(item.action_type);
      const disabled = !item.action_type || (isGrnTile && isGrnPaymentsDisabled);
      const iconKey = item.id || item.icon_url || item.title;
      const icon = item.icon_url && !failedIconIds[iconKey] ? item.icon_url : "";

      return {
        id: item.id,
        iconKey,
        label: item.title,
        icon,
        count: getTileCount(item.id),
        badge: disabled ? null : isGrnTile && hasHomePagePopup ? renderNotificationBadge() : renderBadge(getTileCount(item.id)),
        disabled,
        onClick: disabled ? undefined : () => handleSectionTileClick(item),
      };
    }).filter(Boolean)
    : [];
  const shouldShowManageOrders = orderTiles.length > 0;

  return (
    <React.Fragment>
      <Header
        showLogout={false}
        showProfile={true}
        handleProfile={handleProfileNavigate}
        showSubHeader={showSubHeader}
        sellerDetail={currentSellerDetail}
        allSellerDetails={allSellersDetails}
      />
      <SushiModal visible={showTncModal} isCentered>
        <FlexColumn className="align-items-center outer-box">
          <span className="fs-21 fw-700 my-4 text-align-center">
            {homePagePopUp?.heading}
          </span>

          <span
            className="fs-14 fw-400 text-gray-cool text-box text-align-center"
            dangerouslySetInnerHTML={{ __html: homePagePopUp?.cta?.Text }}>
          </span>

          <Button
            className="fs-13 text-white fw-600 mb-3"
            onClick={() => handleConfirmGrnCTA()}
            disabled={false}
          >
            {showButtonLoader ?
              <Spinner color={"white"} />
              :
              homePagePopUp?.footer_text
            }
          </Button>
        </FlexColumn>
      </SushiModal>
      {showLoader ? (
        <Loader />
      ) : (
        <div className="header-content">
          <HomeSurface>
            <HomeContent>
              {shouldShowHomeSkeleton ? (
                <HomeSkeleton>
                  <SkeletonBlock $height="148px" />
                  <SkeletonBlock $height="300px" />
                </HomeSkeleton>
              ) : null}

              {shouldShowHeroCarousel ? (
                <HeroCarousel>
                  <HeroViewport ref={heroEmblaRef}>
                    <HeroTrack>
                      {bannerSlides.map((banner, index) => (
                        <HeroSlide key={`${banner.id || "banner"}-${index}`}>
                          <HeroCard
                            onClick={() => handleBannerClick(banner)}
                            disabled={!banner.action_type}
                            $backgroundImage={banner.image_url}
                          >
                            <HeroCopy>
                              {banner.subtitle ? <HeroEyebrow>{banner.subtitle}</HeroEyebrow> : null}
                              {banner.title ? <HeroTitle>{banner.title}</HeroTitle> : null}
                              {banner.cta_text ? (
                                <HeroCta>
                                  {banner.cta_text} <ChevronRight size={24} strokeWidth={2.6} />
                                </HeroCta>
                              ) : null}
                            </HeroCopy>
                          </HeroCard>
                        </HeroSlide>
                      ))}
                    </HeroTrack>
                  </HeroViewport>
                  {bannerSlides.length > 1 ? (
                    <HeroDots aria-hidden="true">
                      {bannerSlides.map((banner, index) => (
                        <button
                          key={banner.id || index}
                          type="button"
                          data-active={index === selectedBannerIndex % bannerSlides.length}
                          onClick={() => handleBannerDotClick(index)}
                        >
                          <span
                            style={{
                              transform: `scaleX(${index === selectedBannerIndex % bannerSlides.length ? bannerProgress : 0})`,
                            }}
                          />
                        </button>
                      ))}
                    </HeroDots>
                  ) : null}
                </HeroCarousel>
              ) : null}

              {shouldShowManageOrders ? (
                <ManageOrdersCard>
                  {iconGridSection?.title ? <SectionTitle>{iconGridSection.title}</SectionTitle> : null}
                  <OrdersGrid>
                    {orderTiles.map((tile) => (
                      <OrderTile key={tile.id || tile.icon} onClick={tile.onClick} disabled={tile.disabled}>
                        <TileIcon>
                          {tile.icon ? (
                            <img
                              src={tile.icon}
                              alt=""
                              onError={() => setFailedIconIds((prev) => ({ ...prev, [tile.iconKey]: true }))}
                            />
                          ) : (
                            <TileIconFallback>
                              <ImageOff size={24} strokeWidth={2} />
                            </TileIconFallback>
                          )}
                          {tile.badge || renderBadge(tile.count)}
                        </TileIcon>
                        <span>{tile.label}</span>
                      </OrderTile>
                    ))}
                  </OrdersGrid>
                </ManageOrdersCard>
              ) : null}

              {!isNonTradeVendor && po.length > 0 ? (
                <WhiteWrapper className="invoice-form mt-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fs-16 w-600 fw-600 text-align-r">
                      Pending deliveries
                    </span>
                    <span
                      className="fs-16 w-600 fw-600 text-red-500"
                      onClick={checkAllDDPOs}
                    >
                      See all
                    </span>
                  </div>
                  <SeparatorLine />
                  {po.map((deliveryPo, index) => (
                    <div key={deliveryPo.id || index}>
                      <div
                        className="d-flex align-items-center justify-content-between"
                        onClick={() => naviagetToDelieryPage(deliveryPo.id, "pod")}
                      >
                        <div className="circle-small d-flex flex-column">
                          <span className="fs-9 fw-600 align-self-center mt-3">
                            {getDateMonthFromString(deliveryPo.po_consideration_date, "day")}
                          </span>
                          <span className="fs-9 fw-600 align-self-center">
                            {getDateMonthFromString(deliveryPo.po_consideration_date, "month")}
                          </span>
                        </div>
                        <div className="d-flex flex-column mr-4 ml-4">
                          <span className="fs-15 fw-700">
                            {deliveryPo.purchase_order_number}
                          </span>
                          <span className="fs-12 fw-400 text-gray-cool mt-1">
                            {deliveryPo.purchase_order_items.length} product
                            {deliveryPo.purchase_order_items.length > 1 ? "s" : ""}
                            {" "}•{" "}
                            {calculateTonnage(deliveryPo.purchase_order_items)}
                            {" "}
                            {getWeightUnit(deliveryPo.purchase_order_items)}
                          </span>
                        </div>
                        <span>
                          <img src={"/logos/front_logo.svg"} alt="" className="ml-5" />
                        </span>
                      </div>
                      {index !== po.length - 1 ? <SeparatorLine /> : <></>}
                    </div>
                  ))}
                </WhiteWrapper>
              ) : null}

              <QuickActionsCard>
                <QuickActionsTitle>Quick actions</QuickActionsTitle>
                <QuickActionRow
                  type="button"
                  onClick={handleManageUsersNavigate}
                >
                  <img
                    className="manage-users-icon"
                    src="/logos/users.svg"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>Manage users</span>
                  <ChevronRight aria-hidden="true" />
                </QuickActionRow>
                <QuickActionRow
                  type="button"
                  onClick={handleDownloadLabelsNavigate}
                >
                  <img
                    className="download-labels-icon"
                    src="/logos/download.svg"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>Download packaging labels</span>
                  <ChevronRight aria-hidden="true" />
                </QuickActionRow>
                <QuickActionRow type="button" onClick={handleGetHelp}>
                  <CircleHelp aria-hidden="true" />
                  <span>Get help</span>
                  <ChevronRight aria-hidden="true" />
                </QuickActionRow>
              </QuickActionsCard>
            </HomeContent>
          </HomeSurface>
          <Footer />
        </div>
      )}
      {showToast ?
        <ShowToast
          errorMsg={toastMsg}
          toastMsg={toastMsg}
        />
        :
        <></>
      }
    </React.Fragment>
  );
};

export default HomePage;
